import { toast } from "react-hot-toast";
import { useAsyncStateSupabaseCallback } from "../../../utils/hooks/use-async-state-callback";
import { uuidv4 } from "../../../utils/uuid";
import { SupabaseProgramService } from "../../programs/server/supabase-program-service";

import type { UUID } from "../../../utils/uuid";
import type { ProgramNode, ProgramTree } from "../../program-tree/program-tree";
import type { ProgramLook } from "./use-update-program";

/**
 * Makes a folder in a program, or a top-level program with no parent, after the others at its
 * level, and says if it failed. Resolves to the new id, which it makes itself. An admin can make
 * either; a contributor can make a folder in a program that they can edit. A folder gets the access
 * of the program that it is in; a top-level program is for admins only.
 */
export function useCreateProgram(): {
  create: (
    tree: ProgramTree,
    parent: ProgramNode | null,
    look: ProgramLook,
  ) => Promise<UUID | undefined>;
  creating: boolean;
} {
  const { load: create, loading: creating } = useAsyncStateSupabaseCallback(
    async (
      { supabase },
      tree: ProgramTree,
      parent: ProgramNode | null,
      look: ProgramLook,
    ): Promise<UUID | undefined> => {
      const siblings = parent ? parent.children : tree.roots;
      const order = Math.max(0, ...siblings.map((sibling) => sibling.order)) + 1;
      const id = uuidv4();
      const { error } = await new SupabaseProgramService(supabase).insertWithoutRead({
        ...look,
        id,
        parent_id: parent?.id ?? null,
        order,
      });

      if (error) {
        toast.error(
          `Something went wrong creating the ${parent ? "folder" : "program"}. Please try again.`,
        );
        return undefined;
      }

      return id;
    },
    [],
  );

  return { create, creating };
}
